import Toast from 'react-native-toast-message';

import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import type {CartStore} from '../../../stores/cartStore';
import type {UserStore} from '../../../stores/userStore';
import {parseCheckoutError} from '../../../stores/utils/checkoutErrorRecovery';
import apiService from '../../../transport/apiService';
import {logger} from '../../../utils/logger';
import {computeOrderValidationError} from '../hooks/usePaymentValidation';

/**
 * Validation for checkout flow steps (checkout, review, payment).
 * Uses the CANONICAL computeOrderValidationError from usePaymentValidation.
 *
 * Exclusive offer issues are NOT gated here — MFL order validation at payment
 * catches expired/invalid offers and returns 422 with recovery_action: 'clear_deals'.
 *
 * Note: Cart step bypasses this entirely - uses direct navigation since
 * server-side validateOrder() fails without time slot selected.
 *
 * @param skipServerValidation - Skip server-side validateOrder() call.
 *   Use on checkout/review steps where time slot may not be selected yet.
 */
const handleContinue =
  ({
    callback,
    cartStore,
    userStore,
    skipServerValidation = false,
  }: {
    callback: () => void;
    cartStore: CartStore;
    userStore: UserStore;
    skipServerValidation?: boolean;
  }) =>
  async () => {
    // Step 1: Client-side validation (synchronous)
    const error = computeOrderValidationError(cartStore, userStore);

    if (error) {
      logger.warn('CheckoutValidation', 'Order validation failed', {error});
      Toast.show({
        type: ERROR_MESSAGE,
        text1: error,
      });
      return;
    }

    // Step 2: Server-side validation (async)
    // Skip on checkout/review steps where time slot may not be selected yet
    if (skipServerValidation) {
      callback?.();
      return;
    }

    try {
      await apiService.validateOrder();

      // Validation passed - proceed with navigation
      callback?.();
    } catch (err) {
      const parsedError = parseCheckoutError(err);
      const isExpiredOffer =
        parsedError?.isRecoverable && parsedError?.recoveryAction === 'clear_deals';

      if (isExpiredOffer) {
        // DIS-1220: deal is already cleared server-side at submit-time recovery.
        // Pre-checkout, we silently clean local state and proceed — banner is
        // reserved for genuine submit-time 422s where the user is mid-payment.
        try {
          await apiService.clearOrderDeals();
          cartStore.activatedDeals = [];
          await cartStore.loadOrderForUser();
          logger.info('CheckoutValidation', 'Auto-cleared expired deal pre-checkout');
        } catch {
          // Clear failed — proceed anyway, MFL will catch it at payment
          logger.warn('CheckoutValidation', 'Failed to auto-clear expired deals');
        }
        callback?.();
      } else {
        // Non-recoverable error - show toast and don't proceed
        const message = parsedError?.message || 'Unable to proceed. Please try again.';
        Toast.show({
          type: ERROR_MESSAGE,
          text1: message,
        });
      }
    }
  };

export {handleContinue};
